import {
  buildSchemaIndex,
  fieldOutputType,
  getExplorerChildFields,
  getFieldArgs,
  getFieldOutputTypeName,
  getQueryRootFields,
  isCompositeField,
  isScalarKind,
  unwrapGraphQLType,
} from './schemaTypes';

export const MAX_SELECTABLE_PATH_DEPTH = 8;

export const MAX_SELECTABLE_PATHS = 2000;

/** All selectable field paths under a node (includes the node itself). Stops at cyclic object types. */
export function collectSelectablePaths(
  field,
  schemaIndex,
  basePath,
  visitedTypes = new Set(),
  depth = 0,
) {
  const paths = [basePath];
  const typeName = getFieldOutputTypeName(field);
  if (!typeName || !isCompositeField(field, schemaIndex) || depth >= MAX_SELECTABLE_PATH_DEPTH) {
    return paths;
  }
  if (visitedTypes.has(typeName)) {
    return paths;
  }

  const nextVisited = new Set(visitedTypes);
  nextVisited.add(typeName);

  for (const child of getExplorerChildFields(field, schemaIndex)) {
    const childPaths = collectSelectablePaths(
      child,
      schemaIndex,
      `${basePath}.${child.name}`,
      nextVisited,
      depth + 1,
    );
    for (const childPath of childPaths) {
      paths.push(childPath);
      if (paths.length >= MAX_SELECTABLE_PATHS) {
        return paths;
      }
    }
  }
  return paths;
}

/** Checkbox state: checked = this field selected; indeterminate = some descendants selected. */
export function getFieldCheckboxState(field, schemaIndex, path, selectedPaths) {
  void field;
  void schemaIndex;
  if (selectedPaths.has(path)) return 'checked';

  const prefix = `${path}.`;
  for (const p of selectedPaths) {
    if (p.startsWith(prefix)) return 'indeterminate';
  }
  return 'unchecked';
}

export function defaultArgValue(arg) {
  const t = fieldOutputType(arg?.type);
  if (!t) return 'null';
  switch (t.name) {
    case 'String':
    case 'ID':
      return '""';
    case 'Int':
      return '0';
    case 'Float':
      return '0';
    case 'Boolean':
      return 'false';
    default:
      return 'null';
  }
}

/** Format a user-entered argument value for insertion into a GraphQL query. */
export function formatArgValueForGraphQL(arg, rawValue) {
  if (rawValue == null || String(rawValue).trim() === '') {
    return defaultArgValue(arg);
  }
  const trimmed = String(rawValue).trim();
  const inner = unwrapGraphQLType(arg?.type);
  if (!inner) return `"${trimmed.replace(/"/g, '\\"')}"`;
  if (inner.kind === 'ENUM') return trimmed;
  if (inner.name === 'Int' || inner.name === 'Float') return trimmed;
  if (inner.name === 'Boolean') {
    return ['true', 'false'].includes(trimmed.toLowerCase()) ? trimmed.toLowerCase() : 'false';
  }
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    const innerVal = trimmed.slice(1, -1).replace(/\\"/g, '"');
    return `"${innerVal.replace(/"/g, '\\"')}"`;
  }
  return `"${trimmed.replace(/"/g, '\\"')}"`;
}

export function getEnumTypeName(type) {
  const inner = unwrapGraphQLType(type);
  return inner?.kind === 'ENUM' ? inner.name : null;
}

export function isEnumArg(arg) {
  return !!getEnumTypeName(arg?.type);
}

export function isBooleanArg(arg) {
  return unwrapGraphQLType(arg?.type)?.name === 'Boolean';
}

/** Enum value names for an arg or type reference. */
export function getEnumValues(typeName, schemaIndex) {
  if (!typeName) return [];
  const typeDef = schemaIndex.get(typeName);
  if (!typeDef || typeDef.kind !== 'ENUM') return [];
  return (typeDef.enumValues || []).map((v) => v.name).filter(Boolean);
}

export function getArgEnumValues(arg, schemaIndex) {
  const enumTypeName = getEnumTypeName(arg?.type);
  return enumTypeName ? getEnumValues(enumTypeName, schemaIndex) : [];
}

export function isScalarArg(arg) {
  const inner = unwrapGraphQLType(arg?.type);
  return inner?.kind === 'SCALAR' || inner?.kind === 'ENUM';
}

export function formatFieldArgs(field, argValues, fieldPath) {
  const args = getFieldArgs(field);
  if (!args.length) return '';

  const parts = [];
  for (const a of args) {
    const key = `${fieldPath}.arg.${a.name}`;
    const raw = argValues?.[key];
    if (raw == null || String(raw).trim() === '') continue;
    parts.push(`${a.name}: ${formatArgValueForGraphQL(a, raw)}`);
  }
  return parts.length ? `(${parts.join(', ')})` : '';
}

export function fieldWithArgs(field, fieldPath, argValues) {
  return `${field.name}${formatFieldArgs(field, argValues, fieldPath)}`;
}

export function indent(level) {
  return '  '.repeat(level);
}

export const MAX_GENERATED_QUERY_DEPTH = 8;

export function buildFieldSelection(
  field,
  schemaIndex,
  pathPrefix,
  selectedPaths,
  argValues,
  visitedTypes,
  depth,
  pathDepth
) {
  if (pathDepth > MAX_GENERATED_QUERY_DEPTH) return null;
  const fieldPath = pathPrefix ? `${pathPrefix}.${field.name}` : field.name;
  const hasSelf = selectedPaths.has(fieldPath);
  const childPrefix = fieldPath;
  const typeName = getFieldOutputTypeName(field);

  const childFields = getExplorerChildFields(field, schemaIndex);
  if (!childFields.length) {
    return hasSelf ? `${indent(depth)}${fieldWithArgs(field, fieldPath, argValues)}` : null;
  }

  if (typeName && visitedTypes.has(typeName)) {
    return hasSelf
      ? `${indent(depth)}${fieldWithArgs(field, fieldPath, argValues)} {\n${indent(depth + 1)}__typename\n${indent(depth)}}`
      : null;
  }

  const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;
  const childLines = [];
  for (const child of childFields) {
    const line = buildFieldSelection(
      child,
      schemaIndex,
      childPrefix,
      selectedPaths,
      argValues,
      nextVisited,
      depth + 1,
      pathDepth + 1
    );
    if (line) childLines.push(line);
  }

  if (!childLines.length) {
    return hasSelf
      ? `${indent(depth)}${fieldWithArgs(field, fieldPath, argValues)} {\n${indent(depth + 1)}__typename\n${indent(depth)}}`
      : null;
  }

  return `${indent(depth)}${fieldWithArgs(field, fieldPath, argValues)} {\n${childLines.join('\n')}\n${indent(depth)}}`;
}

/**
 * Build a GraphQL query string from checked explorer paths (e.g. "countries.code").
 */
export function buildQueryFromSelectedPaths(selectedPaths, schema, argValues = {}) {
  if (!schema || !selectedPaths?.size) return '';

  const schemaIndex = buildSchemaIndex(schema);
  const rootFields = getQueryRootFields(schema, schemaIndex);
  const lines = [];

  for (const field of rootFields) {
    const hasDescendant = [...selectedPaths].some(
      (p) => p === field.name || p.startsWith(`${field.name}.`)
    );
    if (!hasDescendant) continue;
    const line = buildFieldSelection(field, schemaIndex, '', selectedPaths, argValues, new Set(), 1, 1);
    if (line) lines.push(line);
  }

  if (!lines.length) return '';
  return `query {\n${lines.join('\n')}\n}`;
}

/**
 * Build a simple starter query from an introspection __schema object.
 * Prefers list root fields (e.g. `countries`) and selects a few scalar subfields.
 */
export function buildStarterQueryFromSchema(schema) {
  if (!schema?.queryType?.name) return '';

  const types = schema.types || [];
  const queryType = types.find(
    (t) => t.name === schema.queryType.name && (t.kind === 'OBJECT' || t.kind === 'INTERFACE')
  );
  if (!queryType?.fields?.length) {
    return 'query {\n  __typename\n}';
  }

  const rootFields = queryType.fields.filter((f) => !f.name.startsWith('__'));
  if (!rootFields.length) {
    return 'query {\n  __typename\n}';
  }

  const listField = rootFields.find((f) => {
    let t = f.type;
    while (t?.kind === 'NON_NULL') t = t.ofType;
    return t?.kind === 'LIST';
  });
  const field = listField || rootFields[0];
  const output = fieldOutputType(field.type);
  const typeDef = output?.name
    ? types.find((t) => t.name === output.name && (t.kind === 'OBJECT' || t.kind === 'INTERFACE'))
    : null;

  let selection = '';
  if (typeDef?.fields?.length) {
    const subfields = typeDef.fields
      .filter((f) => {
        const out = fieldOutputType(f.type);
        return out && isScalarKind(out.kind);
      })
      .slice(0, 4)
      .map((f) => f.name);
    if (subfields.length) {
      selection = ` {\n    ${subfields.join('\n    ')}\n  }`;
    }
  }

  const args = (field.args || []).filter((a) => a.name);
  let argStr = '';
  if (args.length) {
    const first = args[0];
    const argType = fieldOutputType(first.type);
    if (argType?.name === 'String' || argType?.name === 'ID') {
      argStr = `(${first.name}: "US")`;
    } else if (argType?.name === 'Int') {
      argStr = `(${first.name}: 1)`;
    }
  }

  return `query {\n  ${field.name}${argStr}${selection}\n}`;
}
